import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Lock } from 'lucide-react';
import { AdminModal } from './AdminModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);

  // Contenu multilingue pour l'Ancrage Global
  const globalAnchoring = {
    fr: {
      title: 'Ancrage Global',
      subtitle: 'Nos Sièges Sociaux',
      headquarters: [
        { country: 'France', flag: '🇫🇷', city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: 'Cameroun', flag: '🇨🇲', city: 'Yaoundé', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: 'Guinée-Bissau', flag: '🇬🇼', city: 'Bissau', address: 'Rua de Clinica de Bor/Bor' }
      ]
    },
    en: {
      title: 'Global Anchoring',
      subtitle: 'Our Headquarters',
      headquarters: [
        { country: 'France', flag: '🇫🇷', city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: 'Cameroon', flag: '🇨🇲', city: 'Yaoundé', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: 'Guinea-Bissau', flag: '🇬🇼', city: 'Bissau', address: 'Rua de Clinica de Bor/Bor' }
      ]
    },
    pt: {
      title: 'Ancoragem Global',
      subtitle: 'Nossas Sedes',
      headquarters: [
        { country: 'França', flag: '🇫🇷', city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: 'Camarões', flag: '🇨🇲', city: 'Yaoundé', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: 'Guiné-Bissau', flag: '🇬🇼', city: 'Bissau', address: 'Rua de Clinica de Bor/Bor' }
      ]
    },
    it: {
      title: 'Ancoraggio Globale',
      subtitle: 'Le Nostre Sedi',
      headquarters: [
        { country: 'Francia', flag: '🇫🇷', city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: 'Camerun', flag: '🇨🇲', city: 'Yaoundé', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: 'Guinea-Bissau', flag: '🇬🇼', city: 'Bissau', address: 'Rua de Clinica de Bor/Bor' }
      ]
    },
    ru: {
      title: 'Глобальное присутствие',
      subtitle: 'Наши штаб-квартиры',
      headquarters: [
        { country: 'Франция', flag: '🇫🇷', city: 'Рокбрюн-Кап-Мартен', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: 'Камерун', flag: '🇨🇲', city: 'Яунде', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: 'Гвинея-Бисау', flag: '🇬🇼', city: 'Бисау', address: 'Rua de Clinica de Bor/Bor' }
      ]
    },
    zh: {
      title: '全球布局',
      subtitle: '我们的总部',
      headquarters: [
        { country: '法国', flag: '🇫🇷', city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
        { country: '喀麦隆', flag: '🇨🇲', city: '雅温得', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
        { country: '几内亚比绍', flag: '🇬🇼', city: '比绍', address: 'Rua de Clinica de Bor/Bor' }
      ]
    }
  };

  const ga = globalAnchoring[language] || globalAnchoring.fr;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, language });
      toast.success(t('contact.success'));
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error(t('contact.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Global Anchoring Section */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600/20 rounded-full mb-4">
              <MapPin className="w-4 h-4 text-sky-400" />
              <span className="text-sm font-semibold text-sky-400 uppercase tracking-wider">{ga.title}</span>
            </div>
            <h3 className="text-2xl font-bold text-white" style={{fontFamily: 'Playfair Display, serif'}}>
              {ga.subtitle}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ga.headquarters.map((hq, index) => (
              <div 
                key={index} 
                className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 hover:border-sky-500/50 transition-colors"
                data-testid={`headquarters-${index}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{hq.flag}</span>
                  <div>
                    <p className="font-semibold text-white">{hq.country}</p>
                    <p className="text-sm text-sky-400">{hq.city}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {hq.address}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/cfjr0roy_loglenodaFR.png" 
              alt="LENODA Logo" 
              className="h-20 w-auto mb-4 opacity-90"
            />
            <p className="text-sm text-slate-400 leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
              {t('footer.contact')}
            </h3>
            <a 
              href="mailto:contacts@lenoda-services.com" 
              data-testid="footer-email-link"
              className="text-sm text-sky-400 hover:text-sky-300 transition-colors font-medium"
            >
              contacts@lenoda-services.com
            </a>
            <div className="mt-4 space-y-2">
              <Link to="/book-picsu" className="block text-sm text-slate-400 hover:text-white transition-colors">
                {language === 'fr' && 'Ouvrage PICSU'}
                {language === 'en' && 'PICSU Book'}
                {language === 'pt' && 'Livro PICSU'}
                {language === 'it' && 'Opera PICSU'}
                {language === 'ru' && 'Книга PICSU'}
                {language === 'zh' && 'PICSU 著作'}
              </Link>
              <Link to="/scientific-council" className="block text-sm text-slate-400 hover:text-white transition-colors">
                {language === 'fr' && 'Conseil Scientifique'}
                {language === 'en' && 'Scientific Council'}
                {language === 'pt' && 'Conselho Científico'}
                {language === 'it' && 'Consiglio Scientifico'}
                {language === 'ru' && 'Научный совет'}
                {language === 'zh' && '科学委员会'}
              </Link>
              <Link to="/validations" className="block text-sm text-slate-400 hover:text-white transition-colors">
                {language === 'fr' && 'Validations Institutionnelles'}
                {language === 'en' && 'Institutional Validations'}
                {language === 'pt' && 'Validações Institucionais'}
                {language === 'it' && 'Validazioni Istituzionali'}
                {language === 'ru' && 'Институциональные валидации'}
                {language === 'zh' && '机构认证'}
              </Link>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
              {t('footer.newsletter')}
            </h3>
            <form onSubmit={handleNewsletterSubmit} data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.newsletterPlaceholder')}
                data-testid="newsletter-email-input"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 mb-2"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                data-testid="newsletter-submit-button"
                className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? '...' : t('footer.subscribe')}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} LENODA. {t('footer.rights')}
          </p>
          {/* Bouton Admin discret */}
          <button
            onClick={() => setShowAdminModal(true)}
            data-testid="admin-lock-btn"
            className="p-2 text-slate-600 hover:text-slate-400 transition-colors opacity-50 hover:opacity-100"
            title="Administration"
          >
            <Lock className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Admin Modal */}
      <AdminModal isOpen={showAdminModal} onClose={() => setShowAdminModal(false)} />
    </footer>
  );
};